/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { SKIP_INTERCEPT_HEADER } from '@ideapa/app-error';
import {
  LoginDto,
  LoginResponseDto,
  TokenDto,
  UserWithEntiDto,
} from '@ideapa/common-dto';
import { LoginStatusEnum, MFAMethodEnum } from '@ideapa/common-utils';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { of, ReplaySubject } from 'rxjs';
import { catchError, first, map, switchMap, tap } from 'rxjs/operators';
import { AuthFacade } from './+state/auth.facade';
import { AuthConfig } from './auth-config';

const STORAGE_LOCAL_ACCESS_TOKEN_KEY = 'LOCAL_JWT_ACCESS_TOKEN';
const STORAGE_LOCAL_REFRESH_TOKEN_KEY = 'LOCAL_JWT_REFRESH_TOKEN';
const STORAGE_TMP_URL_KEY = 'TMP_URL';
const STORAGE_LOCAL_LOGIN_RESPONSE_KEY = 'LOCAL_LOGIN_RESPONSE';

export enum TokenProvider {
  AAD,
  Internal,
}

@Injectable()
export class AuthService {
  private checkAuthCompleted$ = new ReplaySubject<void>(1);
  private jwtHelper = new JwtHelperService();
  constructor(
    private oidcSecurityService: OidcSecurityService,
    private config: AuthConfig,
    private httpClient: HttpClient,
    private authFacade: AuthFacade
  ) {
    // this.oidcSecurityService.checkAuth().subscribe(() => );
  }

  me() {
    return this.httpClient.get<UserWithEntiDto>(
      this.config.apiEndpoint + '/auth/me'
    );
  }

  // get tokenProvider$() {
  //   return this.oidcSecurityService.isAuthenticated$.pipe(
  //     map((result) => {
  //       if (result.isAuthenticated) {
  //         return TokenProvider.AAD;
  //       } else {
  //         const local_token = this.getLocalAccessToken();
  //         if (!local_token) return null;
  //         const isExpired = this.isJwtTokenExpired(local_token);
  //         if (isExpired) return null;
  //         else return TokenProvider.Internal;
  //       }
  //     }),
  //     catchError(() => of(null))
  //   );
  // }

  get signedIn$() {
    return this.accessToken$.pipe(
      map((token) => {
        if (!token) return false;
        const isExpired = this.isJwtTokenExpired(token);

        if (isExpired) return false;
        return true;
      })
    );
  }

  get accessToken$() {
    return of(this.getLocalAccessToken());
  }

  get userData$() {
    return this.oidcSecurityService.userData$;
  }

  getLoginResponse() {
    const result = localStorage.getItem(STORAGE_LOCAL_LOGIN_RESPONSE_KEY);
    if (result) return JSON.parse(result) as LoginResponseDto;
    return null;
  }

  setLoginResponse(result: LoginResponseDto) {
    localStorage.setItem(
      STORAGE_LOCAL_LOGIN_RESPONSE_KEY,
      JSON.stringify(result)
    );
  }

  checkAuth() {
    return this.oidcSecurityService.checkAuth().pipe(
      switchMap(() => this.accessToken$),
      switchMap((token) => {
        if (!token) return of(false);
        if (this.isJwtTokenExpired(token)) {
          return this.refreshTokens().pipe(
            map((result) => {
              this.authFacade.forceRefreshSuccess(result);
              return true;
            }),
            catchError((err) => {
              console.log('refresh token error ', err);
              return of(false);
            })
          );
        }
        return of(true);
      }),
      first()
    );
  }

  checkAADAuth() {
    return this.oidcSecurityService.checkAuth().pipe(
      // debounceTime(500),
      // switchMap((authResult) => {
      //   if (!authResult.isAuthenticated) {
      //     return this.oidcSecurityService.forceRefreshSession().pipe(
      //       map((refreshResult) => refreshResult.isAuthenticated),
      //       catchError((err) => {
      //         if (authResult?.accessToken) {
      //           return throwError(() => {
      //             new Error('Sessione scaduta');
      //           });
      //         }
      //         return of(false);
      //       })
      //     );
      //   } else {
      //     return of(authResult.isAuthenticated);
      //   }
      // }),
      tap((r) => {
        this.checkAuthCompleted$.next();
      })
      // catchError((err) => {
      //   return throwError(() => {
      //     new Error('Sessione scaduta');
      //   });
      // })
    );
  }

  isAuthenticatedAAD() {
    return this.oidcSecurityService.isAuthenticated();
  }

  loginAAD() {
    return this.oidcSecurityService.authorizeWithPopUp();
  }

  login(
    username: string,
    password: string,
    rememberMe: boolean,
    newPassword?: string
  ) {
    return this.httpClient
      .post<LoginResponseDto>(
        // .post<TokenDto>(
        this.config.apiEndpoint + '/auth/sign-in',
        {
          email: username,
          password,
          rememberMe,
          newPassword,
        },
        {
          headers: new HttpHeaders().set(SKIP_INTERCEPT_HEADER, '1'),
        }
      )
      .pipe(
        tap((result) => {
          if (
            result.loginStatus === LoginStatusEnum.completed &&
            result.token
          ) {
            this.storeTokenResult(result.token);
          }
          localStorage.setItem(
            STORAGE_LOCAL_LOGIN_RESPONSE_KEY,
            JSON.stringify(result)
          );
          // this.storeTokenResult(result);
        })
      );
  }

  switchToken(token: string, rememberMe: boolean) {
    // bearer token in auth
    return this.httpClient
      .post<TokenDto>(
        this.config.apiEndpoint + '/auth/azure-sign-in',
        {
          rememberMe,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      )
      .pipe(
        tap((result) => {
          this.storeTokenResult(result);
        })
      );
  }

  verifyMAOTP(otpCode: string, loginResponse: LoginResponseDto) {
    return this.httpClient
      .post<TokenDto>(
        this.config.apiEndpoint + '/auth/verify-ma-otp',
        {
          otpCode: otpCode,
          loginResponse: loginResponse,
        },
        {
          headers: new HttpHeaders().set(SKIP_INTERCEPT_HEADER, '1'),
        }
      )
      .pipe(
        tap((result) => {
          this.storeTokenResult(result);
        })
      );
  }

  verifyEmailOTP(otpCode: string, loginResponse: LoginResponseDto) {
    return this.httpClient
      .post<TokenDto>(
        this.config.apiEndpoint + '/auth/verify-email-otp',
        {
          otpCode: otpCode,
          loginResponse: loginResponse,
        },
        {
          headers: new HttpHeaders().set(SKIP_INTERCEPT_HEADER, '1'),
        }
      )
      .pipe(
        tap((result) => {
          this.storeTokenResult(result);
        })
      );
  }

  proceedToMFAMethod(
    loginData: LoginDto,
    method: MFAMethodEnum,
    loginResponse: LoginResponseDto
  ) {
    return this.httpClient
      .post<LoginResponseDto>(
        this.config.apiEndpoint + '/auth/proceed-to-mfa-method',
        {
          loginData: loginData,
          method: method,
          loginResponse: loginResponse,
        },
        {
          headers: new HttpHeaders().set(SKIP_INTERCEPT_HEADER, '1'),
        }
      )
      .pipe(
        tap((result) => {
          if (
            result.loginStatus === LoginStatusEnum.completed &&
            result.token
          ) {
            this.storeTokenResult(result.token);
          }
          localStorage.setItem(
            STORAGE_LOCAL_LOGIN_RESPONSE_KEY,
            JSON.stringify(result)
          );
          // this.storeTokenResult(result);
        })
      );
  }

  // logoutAAD() {
  //   return this.oidcSecurityService.logoffAndRevokeTokens();
  // }

  logout() {
    return this.httpClient
      .get<void>(this.config.apiEndpoint + '/auth/logout')
      .pipe(
        switchMap(() => {
          this.clearStorage();
          return this.oidcSecurityService.isAuthenticated$;
        }),
        first(),
        switchMap((result) => {
          if (result.isAuthenticated) {
            return this.oidcSecurityService.logoffAndRevokeTokens();
          }
          return of(true);
        }),
        catchError(() => of(this.clearStorage()))
      );
  }

  refreshTokens() {
    return this.httpClient
      .get<TokenDto>(this.config.apiEndpoint + '/auth/refresh', {
        headers: {
          Authorization: 'Bearer ' + this.getLocalRefreshToken(),
        },
      })
      .pipe(
        tap((result) => {
          this.storeTokenResult(result);
        })
      );
  }

  pushTmpUrl(url: string) {
    sessionStorage.setItem(STORAGE_TMP_URL_KEY, url);
  }

  popTmpUrl() {
    const result = sessionStorage.getItem(STORAGE_TMP_URL_KEY);
    sessionStorage.removeItem(STORAGE_TMP_URL_KEY);
    return result;
  }

  forgotPassword(username: string) {
    return this.httpClient.post(
      this.config.apiEndpoint + '/auth/forgot-password',
      {
        email: username,
      }
    );
  }

  resetPassword(token: string, newPassword: string) {
    return this.httpClient.post(
      this.config.apiEndpoint + '/auth/reset-password',
      { newPassword },
      {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      }
    );
  }

  changePassword(password: string, newPassword: string) {
    return this.httpClient.post(
      this.config.apiEndpoint + '/auth/change-password',
      { password, newPassword }
    );
  }

  private isJwtTokenExpired(jwt: string) {
    return this.jwtHelper.isTokenExpired(jwt);
  }

  private getLocalAccessToken() {
    return localStorage.getItem(STORAGE_LOCAL_ACCESS_TOKEN_KEY);
  }

  private setLocalAccessToken(token: string) {
    localStorage.setItem(STORAGE_LOCAL_ACCESS_TOKEN_KEY, token);
  }

  private setLocalRefreshToken(token: string) {
    localStorage.setItem(STORAGE_LOCAL_REFRESH_TOKEN_KEY, token);
  }

  private getLocalRefreshToken() {
    return localStorage.getItem(STORAGE_LOCAL_REFRESH_TOKEN_KEY);
  }

  private storeTokenResult(result: TokenDto) {
    // quando il login è completo, rimuovo la response
    localStorage.removeItem(STORAGE_LOCAL_LOGIN_RESPONSE_KEY);
    this.setLocalAccessToken(result.accessToken);
    if (result.refreshToken) {
      this.setLocalRefreshToken(result.refreshToken);
    }
  }

  private clearStorage() {
    localStorage.removeItem(STORAGE_LOCAL_ACCESS_TOKEN_KEY);
    localStorage.removeItem(STORAGE_LOCAL_REFRESH_TOKEN_KEY);
    localStorage.removeItem(STORAGE_LOCAL_LOGIN_RESPONSE_KEY);
  }
}
